/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
/**
 * Interface with required and optional parameters for Timeout
 */
export interface ISessionTimeoutProps {
    /**
     * Timeout in ms
     */
    timeout?: number;
    /**
     * Do single or repeating TIN requests, default: false
     */
    recurrent?: boolean;
    /**
     * Callback for timeout test
     * Todo: instead of callback this class should simple emit event!
     */
    callback: (event: any) => void;
    /**
     * Automatically start calling for TIN, default: true
     */
    autostart?: boolean;
    /**
     * Save TIN to timeout, default: true
     */
    saveTinToTimeout?: boolean;
}

export class SessionTimeout {
    /**
     * SessionTimeout in ms and tracking timer
     * Instead of hardcoded constant it should use value from server timeout
     */
    private timeout: number = 3000;
    /**
     * handler returned by window.setTimout
     */
    private timerID?: number = undefined;
    /**
     * Actual TIN Value
     */
    private tin?: number = undefined;
    /**
     * Save tin as timeout
     */
    private saveTinToTimeout: boolean = true;
    /**
     * TIN single or recrurrent check
     */
    private recurrent: boolean = true;
    /**
     * Start immediately after constructor call
     */
    private autostart: boolean = true;
    /**
     * Uri for timeout calls, default: /vdesk/timeoutagent-i.php
     */
    private checkURI: string = '/vdesk/timeoutagent-i.php';
    /**
     * Callback function for TIN
     */
    private callback: (event: any) => void;

    constructor(props: ISessionTimeoutProps) {
        Object.keys(props)
            .filter((k) => props.hasOwnProperty(k))
            .map((k) => (this[k] = props[k]));

        if (this.autostart) {
            this.start();
        }
    }

    /**
     * Start requests to server based on this.timeout
     */
    start() {
        // reset timer if it's called already
        if (this.timerID !== undefined) {
            this.stop();
        }
        // don't ignore
        this.timerID = window.setTimeout(() => {
            const request = new XMLHttpRequest();
            const onerror = () => {
                this.tin = undefined;
                this.callback({
                    message: 'failed request, tin=' + this.tin + ', this.timeout=' + this.timeout,
                    tin: this.tin,
                });
                this.stop();
            };
            request.onerror = onerror;
            request.onload = () => {
                let tin: number | undefined; // undefined
                if (request.status === 0 || request.status >= 400) {
                    onerror();
                } else if (request.status > 0 && request.status < 400) {
                    const matches = document.cookie.match(/TIN=(\d+)[;]?/);
                    if (matches) {
                        tin = parseInt(matches[1], 10);
                    }
                    this.tin = tin;
                    this.callback({
                        message: 'ok, tin=' + this.tin + ', this.timeout=' + this.timeout,
                        tin: this.tin,
                    });
                    if (!tin) {
                        // 0 or undefined
                        this.stop();
                    } else {
                        if (this.saveTinToTimeout) {
                            this.timeout = tin;
                        }
                        if (this.recurrent) {
                            this.start();
                        } else {
                            this.stop();
                        }
                    }
                }
            };
            request.open('GET', this.checkURI);
            request.timeout = this.timeout;
            request.send();
        }, this.timeout);
    }

    /**
     * Stop requests to server
     */
    stop() {
        if (this.timerID !== undefined) {
            window.clearTimeout(this.timerID);
            this.timerID = undefined;
        }
    }
}
